import {
  defined,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
  CallbackProperty,
  Cartesian3,
  SceneTransforms,
  Cartesian2,
} from "@cesium/engine";

import LinestringEntity from "../edit/LinestringEntity";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";

/**
 * 선 엔티티 생성을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for creating line string entities using mouse/keyboard input.
 *
 * @alias InteractionLinestringEntity
 * @constructor
 *
 * @param {object} linestringOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [linestringOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [linestringOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [linestringOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {LinestringEntity} [linestringOption.linestringEntity] 선 엔티티 표시 클래스.</br> Linestring entity display class.
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Linestring Entity' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const linestringEntity = new Ditap.LinestringEntity(viewer);
 *
 * // InteractionLinestringEntity on/off 실행 시 LinestringEntity 초기화 필수
 * // LinestringEntity 초기화 방법
 * // 1. InteractionLinestringEntity 생성 후, LinestringEntity와 callback 함수 대입
 * const interactionLinestringEntity = new Ditap.InteractionLinestringEntity();
 * interactionLinestringEntity.linestringEntity = linestringEntity;
 * interactionLinestringEntity.startCallback = () => {};
 * interactionLinestringEntity.addCallback = () => {};
 * interactionLinestringEntity.endCallback = () => {};
 *
 * // 2. InteractionLinestringEntity 생성 시, linestringOption 지정
 * const linestringOption = {
 *    linestringEntity: linestringEntity,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionLinestringEntity = new Ditap.InteractionLinestringEntity(linestringOption);
 */
class InteractionLinestringEntity {
  constructor(linestringOption) {
    this._moveEntities = []; //move 시 생성되는 Entity Id 배열
    this._infoLabelEntity = undefined;
    this._drawingPolylineEntities = [];
    this._drawingPointEntities = [];
    this._mousePosition = undefined;
    this._handler = undefined;
    this._isOn = false; // 핸들러 실행 여부
    this._windowEvent = {};
    this._warningAlert = undefined;
    this._polylinePrimitive = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    if (linestringOption) {
      if (linestringOption.startCallback instanceof Function) {
        this._startCallback = linestringOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (linestringOption.addCallback instanceof Function) {
        this._addCallback = linestringOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (linestringOption.endCallback instanceof Function) {
        this._endCallback = linestringOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
      if (linestringOption.linestringEntity instanceof LinestringEntity) {
        this._linestringEntity = linestringOption.linestringEntity;
      } else {
        this._linestringEntity = undefined;
      }
    }
  }

  /**
   * 선 엔티티 표시 클래스.</br>
   * Linestring entity display class.
   * @type {LinestringEntity}
   */
  set linestringEntity(linestringEntity) {
    if (
      linestringEntity instanceof LinestringEntity ||
      linestringEntity === undefined
    ) {
      this._linestringEntity = linestringEntity; //원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get linestringEntity() {
    return this._linestringEntity;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 선 생성 중 생성 된 Polyline string Entity 배열. </br>
   * An array of Polyline string Entities created during line string creation.
   * @type {Entity[]}
   */
  set drawingPolylineEntities(drawingPolylineEntities) {
    this._drawingPolylineEntities = drawingPolylineEntities;
  }
  get drawingPolylineEntities() {
    return this._drawingPolylineEntities;
  }

  /*
   * 선 생성 중 생성 된 Point Entity 배열. </br>
   * An array of Point Entities created during line string creation.
   * @type {Entity[]}
   */
  set drawingPointEntities(drawingPointEntities) {
    this._drawingPointEntities = drawingPointEntities;
  }
  get drawingPointEntities() {
    return this._drawingPointEntities;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 윈도우 이벤트를 포함하는 객체</br>
   * Objects containing Windows events.
   * @type {object[]}
   */
  set windowEvent(windowEvent) {
    this._windowEvent = windowEvent;
  }
  get windowEvent() {
    return this._windowEvent;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert.
   * @type {Element}
   */
  set warningAlert(warningAlert) {
    this._warningAlert = warningAlert;
  }
  get warningAlert() {
    return this._warningAlert;
  }

  /*
   * 동적 폴리라인 primitive.</br>
   * Dynamic polyline string primitive
   * @type {Primitive}
   */
  set polylinePrimitive(polylinePrimitive) {
    this._polylinePrimitive = polylinePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point
   * @type {Function}
   */
  set startCallback(startCallback) {
    this._startCallback = startCallback;
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point
   * @type {Function}
   */
  set addCallback(addCallback) {
    this._addCallback = addCallback;
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point
   * @type {Function}
   */
  set endCallback(endCallback) {
    this._endCallback = endCallback;
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 선 엔티티 생성 핸들러 실행.(LinestringEntity 초기화 필수.)</br>
   * Execute linestring entity creation handler. (LinestringEntity initialization is required.)
   */
  on() {
    if (this.linestringEntity && !this.isOn) {
      this.linestringEntity.viewer.currentEvent = this;
      this.isOn = true;
      this._isMobile = Utils.detectDeviceType();

      this.handler = new ScreenSpaceEventHandler(
        this.linestringEntity.viewer.canvas
      );

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.linestringEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 선을 생성하세요",
        undefined,
        true,
        true
      );
      this.infoLabel = Utils.createInfoLabel(
        this.linestringEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 선을 생성하세요",
        undefined,
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createWindowEvent();
      this.createResizeEvent();

      // 경고창 생성
      this.warningAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "선 생성 시, ",
        content2: "두 개 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });
    }
  }

  /**
   * 선 엔티티 생성 핸들러 종료. (LinestringEntity 초기화 필수.)</br>
   * End linestring entity creation handler. (LinestringEntity initialization is required.)
   */
  off() {
    if (this.linestringEntity && this.isOn) {
      const viewer = this.linestringEntity.viewer;

      //핸들러 실행 중인 경우
      // 동적 Entity 제거
      // if (this.dynamicPointEntity) {
      //   viewer.entities.remove(this.dynamicPointEntity);
      // }
      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // move시 생성 Entity 제거
      for (const entity of this.moveEntities) {
        viewer.entities.remove(entity);
      }
      for (const entity of this.drawingPolylineEntities) {
        viewer.entities.remove(entity);
      }
      for (const entity of this.drawingPointEntities) {
        viewer.entities.remove(entity);
      }

      // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        this.removeWindowEvent();
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      this.isOn = false;
      this.entities = [];
      this.positions = [];
      this.moveEntities = [];
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
      // this.dynamicPointEntity = undefined;
      this.drawingPolylineEntities = [];
      this.drawingPointEntities = [];
      this.mousePosition = undefined;
      this.handler = undefined;
      this.windowEvent = {};
      // alert창 삭제
      this.warningAlert.destroy();
      this.warningAlert = undefined;
      this.linestringEntity.viewer.currentEvent = undefined;
      this.polylinePrimitive = undefined;
      this.linestringEntity = undefined;
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removeLastEntities();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const viewer = this.linestringEntity.viewer;
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        if (this.isOn) {
          const isMobile = Utils.detectDeviceType();
          if (this._isMobile !== isMobile) {
            if (isMobile) {
              this._isMobile = true;

              this.cancelBtn = this.mobileInfoLabel.cancelBtn;
              this.endBtn = this.mobileInfoLabel.endBtn;
              this.removeBtn = this.mobileInfoLabel.removeBtn;
              this.infoLabelEntity = this.mobileInfoLabel.label;
              this.infoLabel.style.display = "none";
              this.mobileInfoLabel.label.style.display = "block";

              this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
              for (const entity of this.moveEntities) {
                viewer.entities.remove(entity);
              }
              this.moveEntities = [];

              if (this.polylinePrimitive) {
                viewer.scene.primitives.remove(this.polylinePrimitive);
              }

              this.createMobileEvent();
            } else {
              this.removeMobileEvent();
              this._isMobile = false;
              this.infoLabelEntity = this.infoLabel;
              this.mobileInfoLabel.label.style.display = "none";
              this.infoLabel.style.display = "block";
              this.createMouseMoveHandler();
            }
          }
        }
      })
    );
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(
        e.position,
        this.linestringEntity.viewer
      );
      const positions = this.linestringEntity.positions;

      if (
        defined(position) &&
        !position.equals(positions[positions.length - 1])
      ) {
        this.mousePosition = position;
        // POINT 생성 - MOVE시 생성되는 엔티티
        const point = Utils.createPoint({
          viewer: this.linestringEntity.viewer,
          position: position,
        });
        this.drawingPointEntities.push(point);

        positions.push(position);

        // 점 생성 시 콜백함수 실행
        if (this.addCallback && this.linestringEntity) {
          this.addCallback(this.linestringEntity);
        }

        if (positions.length > 1) {
          const array = [];
          array.push(positions[positions.length - 2]);
          array.push(positions[positions.length - 1]);

          const polyline = Utils.createPolyline({
            viewer: this.linestringEntity.viewer,
            positions: new CallbackProperty(() => array, false),
          });
          this.drawingPolylineEntities.push(polyline);
        } else if (
          positions.length === 1 &&
          this.startCallback &&
          this.linestringEntity
        ) {
          // 첫번째 점 생성 시 콜백함수 실행
          this.startCallback(this.linestringEntity);
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.linestringEntity.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);
      const positions = this.linestringEntity.positions;

      if (defined(position)) {
        this.mousePosition = position;
        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );

        // move 시 생성 된 entities 제거
        for (const entity of this.moveEntities) {
          viewer.entities.remove(entity);
        }
        this.moveEntities = [];

        if (positions.length > 0) {
          const polyline = Utils.createPolyline({
            viewer: viewer,
            positions: new CallbackProperty(() => {
              const left = positions[positions.length - 1];
              const right = position;

              const leftCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                left
              );
              const rightCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                right
              );

              const distance = Cartesian2.distance(leftCart, rightCart);

              const resultArray = [left];
              if (distance < 100) {
                resultArray.push(left);
              } else {
                const middle = Cartesian3.lerp(
                  left,
                  right,
                  9 / 10,
                  new Cartesian3()
                );
                resultArray.push(middle);
              }

              return resultArray;
            }, false),
          });
          this.moveEntities.push(polyline);
        }

        if (this.polylinePrimitive) {
          viewer.scene.primitives.remove(this.polylinePrimitive);
        }

        if (positions.length > 0) {
          const array = [];
          array.push(positions[positions.length - 1]);
          array.push(position);
          const polyline = Utils.createPolylinePrimitive({
            viewer: viewer,
            positions: array,
          });
          this.polylinePrimitive = polyline;
        }
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 더블 클릭 이벤트 핸들러 생성.</br>
   * Create mouse double-click event handler.
   */
  createDoubleClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 직선 거리 측정 종료.<br>
   * End straight line distance measurement.
   */
  endHandler() {
    const positions = this.linestringEntity.positions;
    if (positions.length > 1) {
      setTimeout(() => {
        this.linestringEntity.createEntity();

        // 마지막 점 생성 시 콜백함수 실행
        if (this.endCallback && this.linestringEntity) {
          this.endCallback(this.linestringEntity);
        }

        this.off();
      }, 10);
    } else {
      this.warningAlert.open();
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.linestringEntity.positions.length > 0) {
        this.removeLastEntities();
      } else {
        this.abort();
        this.off();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      this.off();
      this.abort();
    } else if (event.key === "Backspace") {
      this.removeLastEntities();
    }
  }

  /**
   * 생성 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    // viewerStorage에서 삭제
    this.linestringEntity?.viewer.ditapStorage.removeInstanceById(
      this.linestringEntity.id
    );

    // linestringEntity 삭제
    this.linestringEntity?.destroy();

    // this.linestringEntity = undefined;
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마지막으로 생성된 엔티티 제거.</br>
   * Remove last created entity.
   */
  removeLastEntities() {
    // Backspace : 한 점 제거

    const viewer = this.linestringEntity.viewer;
    const position = this.mousePosition;

    if (defined(position)) {
      const positions = this.linestringEntity.positions;
      const pointEntities = this.drawingPointEntities;
      const polylineEntities = this.drawingPolylineEntities;
      const moveEntities = this.moveEntities;

      // 두 점 이상일 때 > 마지막 Entity 제거, 배열에서도 제거
      const point = pointEntities[pointEntities.length - 1];
      // 마지막 point 제거
      this.linestringEntity.removeLastElement(pointEntities);
      this.linestringEntity.removeViewerEntity(point);

      for (const entity of moveEntities) {
        viewer.entities.remove(entity);
      }

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      if (positions.length > 1) {
        // 마지막 polyline 제거
        const polyline = polylineEntities[polylineEntities.length - 1];
        this.linestringEntity.removeLastElement(polylineEntities);
        this.linestringEntity.removeViewerEntity(polyline);
      }

      positions.pop();

      if (!this._isMobile && positions.length > 0) {
        if (positions.length > 0) {
          const array = [];
          array.push(positions[positions.length - 1]);
          array.push(this.mousePosition);
          const polyline = Utils.createPolyline({
            viewer: viewer,
            positions: new CallbackProperty(() => array, false),
          });
          this.moveEntities.push(polyline);
        }
      }
    }
  }

  /*
   * 윈도우 객체에 추가된 이벤트 제거.</br>
   * Remove Events added on Windows Objects
   */
  removeWindowEvent() {
    const keys = Object.keys(this.windowEvent);
    for (let i = 0; i < keys.length; i++) {
      window.removeEventListener(keys[i], this.windowEvent[keys[i]]);
    }
  }

  /*
   * 윈도우 객체에 이벤트 추가.</br>
   * Add events to Window object
   */
  createWindowEvent() {
    // keyDown 이벤트 생성
    window.addEventListener(
      "keydown",
      (this.windowEvent["keydown"] = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}
export default InteractionLinestringEntity;
